import {
  Avatar,
  Button,
  Icon,
  IconButton,
  TextCaption,
  Tooltip,
} from '@pypestream/design-system';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useRepublishModal } from '../../components';
import { formatDateTime } from '../../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import type { SolutionVersion } from '../../xstate/prostudio.api-service.types';
import { filterUniqueVersions } from '../versions';
import { mergeVersionRows } from '../../utils/version-table-rows';
import { AgentChanges, CountChips } from './diff-parts';
import { diffSolutions, totalChanges, versionNumber } from './solution-diff';
import { useSolutionVersionId, useVersionRows } from './use-version-rows';
import { pickVisibleVersions } from './visible-versions';

type MergedVersion = SolutionVersion & { env: string };

/** How many more published versions each "Show more" renders. */
const VERSIONS_PAGE = 20;

/** A past version's changes against the version published before it. */
const VersionDetail: FC<{
  version: string;
  previous: string | null;
}> = ({ version, previous }) => {
  const toId = useSolutionVersionId();
  const current = useVersionRows(toId(version));
  const before = useVersionRows(previous ? toId(previous) : null);

  if (current.status === 'error' || before.status === 'error') {
    return (
      <TextCaption variant="danger">
        Couldn&apos;t load this version&apos;s nodes:{' '}
        {current.error ?? before.error}
      </TextCaption>
    );
  }
  if (!current.rows || (previous && !before.rows)) {
    return <TextCaption variant="secondary">Loading changes…</TextCaption>;
  }

  const diff = diffSolutions(before.rows ?? [], current.rows);
  const total = totalChanges(diff.counts);
  return (
    <div className="c-deploy-version__detail">
      <div className="c-deploy-eyebrow">
        {previous
          ? `What changed since ${previous}, by agent`
          : 'First published version'}
      </div>
      <div className="c-deploy-panel__sub">
        <TextCaption variant="secondary">
          {total} node{total === 1 ? '' : 's'}{' '}
          {previous ? 'changed' : 'published'}
        </TextCaption>
        <CountChips counts={diff.counts} />
      </div>
      <AgentChanges agents={diff.agents} />
    </div>
  );
};

export const PublishedVersions: FC<{ isViewer: boolean }> = ({ isViewer }) => {
  const { versions, deployedVersions } = useProStudioCtxSelector((ctx) => ({
    versions: ctx.versions,
    deployedVersions: ctx.deployedVersions,
  }));
  const [open, setOpen] = useState<string | null>(null);
  const [limit, setLimit] = useState(VERSIONS_PAGE);
  // The first row a "Show more" revealed: focus moves there, so a keyboard
  // user lands on the new rows instead of losing focus with the button.
  const [revealedFrom, setRevealedFrom] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [republishing, setRepublishing] = useState<SolutionVersion>();
  const { republishModal, openModal: openRepublishModal } = useRepublishModal({
    botVersion: republishing,
  });

  const list = useMemo(
    () =>
      (
        mergeVersionRows(
          filterUniqueVersions(versions || [])
        ) as MergedVersion[]
      ).sort((a, b) => versionNumber(b.version) - versionNumber(a.version)),
    [versions]
  );

  const isCurrent = (version: string | null, env: string) =>
    !!version && deployedVersions?.[env as 'sandbox' | 'live'] === version;

  // The versions the environments run and the open row stay rendered however
  // far back they are.
  const { rows, hidden, next } = pickVisibleVersions(list, {
    limit,
    page: VERSIONS_PAGE,
    pinned: (version) =>
      version === open ||
      isCurrent(version, 'sandbox') ||
      isCurrent(version, 'live'),
  });

  useEffect(() => {
    if (revealedFrom === null) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-version-index="${revealedFrom}"]`)
      ?.focus();
    setRevealedFrom(null);
  }, [revealedFrom]);

  return (
    <div
      ref={listRef}
      className="c-deploy-versions"
      data-testid="bobcat-deploy-versions"
    >
      <div className="c-deploy-eyebrow">Published versions</div>
      {list.length > 0 && (
        <div className="c-deploy-version__row c-deploy-version__row--head">
          <span className="c-deploy-version__toggle">Version</span>
          <span className="c-deploy-version__envs">Environment</span>
          <span className="c-deploy-version__by">Last published</span>
          <span className="c-deploy-version__actions" />
        </div>
      )}
      {!list.length && (
        <TextCaption variant="secondary">
          Nothing published yet. Publish the draft to create the first version.
        </TextCaption>
      )}
      {rows.map(({ item, index: i }) => {
        const key = item.version ?? String(i);
        const isOpen = open === key;
        const previous = list[i + 1]?.version ?? null;
        const name = `${item.first_name} ${item.last_name}`.trim();
        return (
          <div
            key={key}
            className="c-deploy-version"
            data-testid="bobcat-deploy-version"
          >
            <div className="c-deploy-version__row">
              <button
                type="button"
                className="c-deploy-version__toggle"
                aria-expanded={isOpen}
                data-version-index={i}
                onClick={() => setOpen(isOpen ? null : key)}
              >
                <Icon
                  name={isOpen ? 'chevron-down' : 'chevron-right'}
                  size="xsmall"
                />
                {item.version}
              </button>
              <span className="c-deploy-version__envs">
                {item.env.split(', ').map((env) => (
                  <span
                    key={env}
                    className={`c-deploy-env-tag c-deploy-env-tag--${env}${
                      isCurrent(item.version, env) ? ' is-current' : ''
                    }`}
                  >
                    <span className="c-deploy-env__dot" />
                    {env === 'live' ? 'Live' : 'Sandbox'}
                  </span>
                ))}
              </span>
              <span className="c-deploy-version__by">
                {name && (
                  <Avatar size="small" src={item.picture} label={name} />
                )}
                <span>
                  {name}
                  {name && item.time ? ' · ' : ''}
                  {item.time ? formatDateTime(item.time) : ''}
                </span>
              </span>
              <span className="c-deploy-version__actions">
                {!isViewer && (
                  <Tooltip content="Republishing a version will not replace nodes on the workflows page">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        setRepublishing(item);
                        openRepublishModal();
                      }}
                    >
                      <Icon name="deploy" slot="prefix" size="xsmall" />
                      Republish
                    </Button>
                  </Tooltip>
                )}
                <Tooltip content="Download solution">
                  <IconButton
                    name="download"
                    size="small"
                    onClick={() =>
                      sendProStudioEvent({
                        type: 'getVersionFile',
                        version: item.version ?? null,
                      })
                    }
                  />
                </Tooltip>
              </span>
            </div>
            {isOpen && item.version && (
              <VersionDetail version={item.version} previous={previous} />
            )}
          </div>
        );
      })}
      {hidden > 0 && (
        <Button
          variant="ghost"
          size="small"
          data-testid="bobcat-deploy-versions-more"
          onClick={() => {
            setRevealedFrom(limit);
            setLimit(limit + VERSIONS_PAGE);
          }}
        >
          Show {next} more version{next === 1 ? '' : 's'}
        </Button>
      )}
      {republishModal}
    </div>
  );
};
